import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import PhoneInput from 'react-phone-input-2';
import toast from 'react-hot-toast';
import 'react-phone-input-2/lib/style.css';

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, user, loading } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({}); // Track which fields have been touched

  // Password strength validation - memoized to avoid recalculation
  const getPasswordStrength = (password) => {
    try {
      if (!password) {
        return { 
          checks: {}, 
          score: 0, 
          strength: 'weak',
          suggestions: []
        };
      }
      
      const checks = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /\d/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      };

      const passedChecks = Object.values(checks).filter(Boolean).length;
      
      const suggestions = [];
      if (!checks.length) suggestions.push('Use at least 8 characters');
      if (!checks.uppercase) suggestions.push('Add uppercase letters (A-Z)');
      if (!checks.lowercase) suggestions.push('Add lowercase letters (a-z)');
      if (!checks.number) suggestions.push('Add numbers (0-9)');
      if (!checks.special) suggestions.push('Add special characters (!@#$%...)');
      
      return {
        checks,
        score: passedChecks,
        strength: passedChecks < 3 ? 'weak' : passedChecks < 5 ? 'medium' : 'strong',
        suggestions,
      };
    } catch (error) {
      console.error('Error in getPasswordStrength:', error);
      return { 
        checks: {}, 
        score: 0, 
        strength: 'weak',
        suggestions: []
      };
    }
  };

  // Calculate current password strength
  const currentPasswordStrength = getPasswordStrength(formData.password);

  // Handle input change with real-time validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Real-time validation for password fields
    if (name === 'password' || name === 'confirmPassword') {
      // Clear errors for real-time feedback
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        
        // If changing password, also revalidate confirmPassword
        if (name === 'password' && formData.confirmPassword) {
          if (value !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
          } else {
            delete newErrors.confirmPassword;
          }
        }
        
        // If changing confirmPassword, validate against password
        if (name === 'confirmPassword') {
          if (value !== formData.password) {
            newErrors.confirmPassword = 'Passwords do not match';
          } else {
            delete newErrors.confirmPassword;
          }
        }
        
        return newErrors;
      });
    } else {
      // For other fields, just clear the error
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };
  
  // Handle blur event for validation
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Validate the specific field on blur
    validateField(name);
  };
  
  // Validate a single field
  const validateField = (fieldName) => {
    const fieldErrors = {};
    
    switch (fieldName) {
      case 'firstName':
        if (!formData.firstName.trim()) {
          fieldErrors.firstName = 'First name is required';
        } else if (formData.firstName.trim().length < 2) {
          fieldErrors.firstName = 'First name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s'-]+$/.test(formData.firstName.trim())) {
          fieldErrors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';
        }
        break;
      
      case 'lastName':
        if (!formData.lastName.trim()) {
          fieldErrors.lastName = 'Last name is required';
        } else if (formData.lastName.trim().length < 2) {
          fieldErrors.lastName = 'Last name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s'-]+$/.test(formData.lastName.trim())) {
          fieldErrors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
        }
        break;
      
      case 'email':
        if (!formData.email) {
          fieldErrors.email = 'Email is required';
        } else {
          const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          if (!emailRegex.test(formData.email)) {
            fieldErrors.email = 'Please enter a valid email address';
          } else {
            const disposableDomains = ['tempmail.com', 'throwaway.email', 'guerrillamail.com', 'mailinator.com', '10minutemail.com'];
            const domain = formData.email.split('@')[1]?.toLowerCase();
            if (disposableDomains.includes(domain)) {
              fieldErrors.email = 'Temporary email addresses are not allowed. Please use a business email.';
            }
          }
        }
        break;
      
      case 'company':
        if (!formData.company.trim()) {
          fieldErrors.company = 'Company name is required';
        } else if (formData.company.trim().length < 2) {
          fieldErrors.company = 'Company name must be at least 2 characters';
        }
        break;
      
      case 'password':
        const pwdStrength = getPasswordStrength(formData.password);
        if (!formData.password) {
          fieldErrors.password = 'Password is required';
        } else if (pwdStrength.score < 5) {
          fieldErrors.password = 'Password must meet all requirements: ' + pwdStrength.suggestions.join(', ');
        }
        // Also validate confirm password if it's filled
        if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
          fieldErrors.confirmPassword = 'Passwords do not match';
        } else if (formData.confirmPassword && formData.password === formData.confirmPassword) {
          // Clear confirmPassword error if they now match
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.confirmPassword;
            return newErrors;
          });
        }
        break;
      
      case 'confirmPassword':
        if (!formData.confirmPassword) {
          fieldErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
          fieldErrors.confirmPassword = 'Passwords do not match';
        }
        break;
      
      case 'phone':
        if (formData.phone && formData.phone.trim()) {
          const phoneRegex = /^\+?[1-9]\d{1,14}$/;
          const cleanPhone = formData.phone.trim().replace(/\s+/g, '');
          if (!phoneRegex.test(cleanPhone)) {
            fieldErrors.phone = 'Please enter a valid phone number';
          }
        }
        break;
      
      default:
        break;
    }
    
    setErrors(prev => ({
      ...prev,
      ...fieldErrors
    }));
  };
  
  // Check if form is valid for submit button
  const isFormValid = () => {
    const validationErrors = validateForm();
    return Object.keys(validationErrors).length === 0 && 
           formData.firstName && 
           formData.lastName && 
           formData.email && 
           formData.company && 
           formData.password && 
           formData.confirmPassword;
  };

  // Validate form with enterprise-grade rules
  const validateForm = () => {
    const newErrors = {};
    
    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.firstName.trim())) {
      newErrors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';
    }
    
    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.lastName.trim())) {
      newErrors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
    }
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid business email address';
      }
      // Block disposable email domains
      const disposableDomains = ['tempmail.com', 'throwaway.email', 'guerrillamail.com', 'mailinator.com', '10minutemail.com'];
      const domain = formData.email.split('@')[1]?.toLowerCase();
      if (disposableDomains.includes(domain)) {
        newErrors.email = 'Temporary email addresses are not allowed. Please use a business email.';
      }
    }
    
    // Phone validation (optional but strict if provided)
    if (formData.phone && formData.phone.trim()) {
      const phoneRegex = /^\+?[1-9]\d{1,14}$/;
      const cleanPhone = formData.phone.trim().replace(/\s+/g, '');
      if (!phoneRegex.test(cleanPhone)) {
        newErrors.phone = 'Please enter a valid phone number (e.g., +1234567890)';
      }
    }
    
    // Company validation
    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    } else if (formData.company.trim().length < 2) {
      newErrors.company = 'Company name must be at least 2 characters';
    }
    
    // Password validation - recalculate strength every time
    const currentPasswordStrength = getPasswordStrength(formData.password);
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (currentPasswordStrength.score < 5) {
      newErrors.password = 'Password must meet all requirements: ' + currentPasswordStrength.suggestions.join(', ');
    }
    
    // Confirm Password validation - always check against current password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      const userData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || undefined,
        company: formData.company.trim(),
        password: formData.password,
      };

      const result = await register(userData);
      if (result.success) {
        // Registration successful - show success message
        setErrors({ general: '' });
        toast.success('Registration successful! Please check your email for verification code.', {
          duration: 4000,
          icon: '✅',
        });
        
        // Redirect to email verification
        setTimeout(() => {
          navigate('/verify-email', { 
            state: { email: formData.email.trim() },
            replace: true 
          });
        }, 500);
      } else {
        // Handle registration failure with specific error message from backend
        const errorMsg = result.error || 'Registration failed. Please try again.';
        setErrors({ general: errorMsg });
        toast.error(errorMsg, {
          duration: 5000,
          icon: '❌',
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      // Extract the most specific error message available
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error.response?.data) {
        // Backend sent structured error
        if (error.response.data.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
      toast.error(errorMessage, {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !loading) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, location]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        background: 'linear-gradient(135deg, #197560 0%, #04947A 50%, #0D738E 100%)'
      }}>
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{
      background: 'linear-gradient(135deg, #197560 0%, #04947A 50%, #0D738E 100%)'
    }}>
      {/* Header with Full Logo, separated */}
      <header className="w-full py-4 px-6 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm">
        <div className="w-full max-w-7xl mx-auto">
          <a href="/" className="inline-block hover:opacity-80 transition-opacity cursor-pointer">
            <img 
              src="https://xloud.tech/s3/.Xlogos/Xloud%20Tech%20logo%20high%20Res.svg" 
              alt="Xloud Technologies" 
              className="h-12 w-auto"
              onError={(e) => { e.currentTarget.src = '/logo.svg'; }}
            />
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="w-full py-10 px-6 text-center">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl font-bold text-white mb-3">Create Account</h1>
          <p className="text-white/90">Enterprise License Management Platform</p>
        </div>
      </section>

      {/* Main Content: unified white container with left SVG and right form */}
      <section className="w-full px-6 pb-16">
        <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-2xl p-6 md:p-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Left: Illustration */}
            <div className="flex items-center justify-center md:justify-start">
              <img 
                src="/register-illustration.svg" 
                alt="Create Account Illustration" 
                className="w-full max-w-lg h-auto"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>

            {/* Right: Form */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              {errors.general && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-red-800">{errors.general}</span>
                  </div>
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">First Name *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      required
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`transition-all duration-200 focus:ring-2 focus:ring-opacity-50 ${errors.firstName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'focus:border-primary focus:ring-primary'}`}
                    />
                    {errors.firstName && <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">Last Name *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      required
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`transition-all duration-200 focus:ring-2 focus:ring-opacity-50 ${errors.lastName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'focus:border-primary focus:ring-primary'}`}
                    />
                    {errors.lastName && <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Business Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`transition-all duration-200 focus:ring-2 focus:ring-opacity-50 ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'focus:border-primary focus:ring-primary'}`}
                  />
                  {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <Label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">Company *</Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    autoComplete="organization"
                    required
                    placeholder="Company name"
                    value={formData.company}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`transition-all duration-200 focus:ring-2 focus:ring-opacity-50 ${errors.company ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'focus:border-primary focus:ring-primary'}`}
                  />
                  {errors.company && <p className="mt-2 text-sm text-red-600">{errors.company}</p>}
                </div>

                {/* Phone (optional) */}
                <div>
                  <Label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone (optional)</Label>
                  <PhoneInput
                    country={'in'}
                    value={formData.phone}
                    onChange={(phone) => setFormData(prev => ({ ...prev, phone: '+' + phone }))}
                    onBlur={handleBlur}
                    enableSearch={true}
                    searchPlaceholder="Search country"
                    inputProps={{
                      name: 'phone',
                      required: false,
                      autoFocus: false
                    }}
                    containerStyle={{
                      width: '100%'
                    }}
                    inputStyle={{
                      width: '100%',
                      height: '42px',
                      fontSize: '14px',
                      paddingLeft: '48px',
                      borderRadius: '0.375rem',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      backgroundColor: '#ffffff'
                    }}
                    buttonStyle={{
                      borderRadius: '0.375rem 0 0 0.375rem',
                      backgroundColor: '#f9fafb',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRight: 'none'
                    }}
                    dropdownStyle={{
                      borderRadius: '0.375rem'
                    }}
                  />
                  {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone}</p>}
                </div>

                {/* Passwords */}
                  <div>
                    <Label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        placeholder="Create password"
                        value={formData.password}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className={`pr-12 transition-all duration-200 focus:ring-2 focus:ring-opacity-50 ${
                          errors.password 
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                            : 'focus:border-primary focus:ring-primary'
                        }`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.757 7.757M9.878 9.878l4.242 4.242m0 0L12 12m2.121-2.122L16.243 7.757" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.275 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                    )}

                    {formData.password && currentPasswordStrength && (
                      <div className="mt-2">
                        <div className="flex items-center space-x-2 text-xs">
                          <span className="text-gray-600">Password strength:</span>
                          <span className={`font-medium ${currentPasswordStrength.strength === 'weak' ? 'text-red-600' : currentPasswordStrength.strength === 'medium' ? 'text-yellow-600' : 'text-green-600'}`}>
                            {currentPasswordStrength.strength ? currentPasswordStrength.strength.charAt(0).toUpperCase() + currentPasswordStrength.strength.slice(1) : 'Weak'}
                          </span>
                        </div>
                        <div className="mt-1 flex space-x-1">
                          {[...Array(5)].map((_, i) => (
                            <div key={i} className={`h-1 flex-1 rounded ${i < (currentPasswordStrength.score || 0) ? (currentPasswordStrength.strength === 'weak' ? 'bg-red-500' : currentPasswordStrength.strength === 'medium' ? 'bg-yellow-500' : 'bg-green-500') : 'bg-gray-200'}`} />
                          ))}
                        </div>
                        {currentPasswordStrength.suggestions && currentPasswordStrength.suggestions.length > 0 && (
                          <ul className="mt-2 text-xs text-gray-600 space-y-1">
                            {currentPasswordStrength.suggestions.map((suggestion, i) => (
                              <li key={i}>• {suggestion}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>

                {/* Confirm Password */}
                  <div>
                    <Label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password *
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        placeholder="Confirm password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className={`pr-12 transition-all duration-200 focus:ring-2 focus:ring-opacity-50 ${
                          errors.confirmPassword 
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                            : 'focus:border-primary focus:ring-primary'
                        }`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.757 7.757M9.878 9.878l4.242 4.242m0 0L12 12m2.121-2.122L16.243 7.757" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.275 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (<p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>)}
                  </div>
                

                {/* Submit Button */}
                <div className="flex justify-end pt-4">
                  <Button
                    type="submit"
                    className="px-8 py-3 font-semibold text-white rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    style={{
                      background: isFormValid() && !isSubmitting
                        ? 'linear-gradient(135deg, #197560 0%, #04947A 50%, #0D738E 100%)'
                        : '#9CA3AF',
                    }}
                    disabled={isSubmitting || !isFormValid()}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating Account...
                      </div>
                    ) : (
                      'Submit'
                    )}
                  </Button>
                </div>

                {/* Footer Links */}
                <div className="text-center pt-4 border-t border-gray-100">
                  <div>
                    <span className="text-gray-600 text-sm">Already have an account? </span>
                    <Link 
                      to="/login" 
                      className="text-primary hover:text-primary-dark font-medium transition-colors hover:underline"
                    >
                      Sign in here
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full py-4 px-6 border-t border-white/10 mt-auto">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-sm text-white/70">
            © 2025 Xloud Technologies Pvt Ltd. All rights reserved. | License Management System
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RegisterPage;